import {
	AddressItemType,
	InfiniteData,
	GetItemCounts,
	PaginatedResponseType,
	PostageItemType,
	PostageUsageType,
	useInfinitePostage,
} from '@ukpostbox/client-data';
import {
	AlertBanner,
	Colour,
	ComboBoxControl,
	ComboBoxItemDisplayEnum,
	ComboBoxTypeEnum,
	SelectItemType,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { GetArrayIndexByKeyValue } from '@ukpostbox/helpers';
import React, { useEffect, useState } from 'react';

interface PostageControlType {
	activeAddress: AddressItemType | null;

	name: string;
	postageType: PostageUsageType;

	label?: string;
	placeholder?: string;
	minimal?: boolean;
	defaultValues?: Array<any>;
	singleSelection?: boolean;
	forwardValueCallback?: (value: number) => void;
	forwardValuesCallback?: (values: Array<any>) => void;
	forwardItemCallback?: (value: SelectItemType) => void;

	alwaysEnabled?: boolean;

	loading?: boolean;
	disabled?: boolean;

	reset?: boolean;
	required?: boolean;
	requiredLabel?: string;
}

interface PostageControlInnerType extends PostageControlType {
	currentCountryCode: string;
}

function PostageControlInner(props: PostageControlInnerType) {
	const { t } = useTranslation();

	const {
		status,
		data,
		error,
		isStale,
		isRefetching,
		dataUpdatedAt,
		isFetchingNextPage,
		fetchNextPage,
		hasNextPage,
	} = useInfinitePostage(props.currentCountryCode, props.postageType);

	const [postageDefault, setPostageDefault] = useState<Array<any> | undefined>(props.defaultValues);
	const [postageOptions, setPostageOptions] = useState<Array<SelectItemType>>([]);
	const [postageItemCounts, setPostageItemCounts] = useState<any>({ loaded: 0, total: 0 });
	const [postageError, setPostageError] = useState<string>('');

	const HandleItemsChanged = (e: SelectItemType) => {
		if (props.forwardItemCallback) {
			const postageItem = data?.pages.map(
				(page) => page?.data && page.data.filter((postage) => postage.value === e.value),
			)[0][0];

			if (postageItem) {
				props.forwardItemCallback(postageItem);
			}
		}
	};

	const MapPostageOptions = (newData: InfiniteData<PaginatedResponseType>) => {
		setPostageItemCounts(GetItemCounts(newData));

		let filteredPostage = newData?.pages.map(
			(page: any) =>
				page?.data &&
				page.data.map((postage: PostageItemType) => ({
					label: postage.label,
					value: postage.value,
				})),
		);

		filteredPostage = filteredPostage.flat();

		if (props.defaultValues) {
			const valueExists = GetArrayIndexByKeyValue(filteredPostage, 'value', props.defaultValues[0]);

			if (valueExists === -1) {
				setPostageError('The selected Postage Option is not available for this address');
				setPostageDefault(undefined);
			} else {
				HandleItemsChanged(props.defaultValues[0]);
				setPostageError('');
				setPostageDefault(props.defaultValues);
			}

			setPostageDefault(valueExists === -1 ? undefined : props.defaultValues);
		} else {
			setPostageDefault(undefined);
		}

		setPostageOptions(filteredPostage);
	};

	const HandleValueChanged = (e: any) => {
		if (props.forwardValuesCallback) {
			props.forwardValuesCallback(e);
		}

		if (props.forwardValueCallback) {
			props.forwardValueCallback(e);
		}
	};

	useEffect(() => {
		if (data) {
			MapPostageOptions(data);
		}
	}, [dataUpdatedAt, props.currentCountryCode]);

	if (status === 'error') {
		return <AlertBanner title={error?.message} colour={Colour.Alert} />;
	}

	return (
		<>
			<ComboBoxControl
				name={props.name}
				placeholder={props.placeholder || t('controls.postagePlaceholder')}
				data={postageOptions}
				defaultValue={postageError === '' && postageOptions.length > 0 ? postageDefault : undefined}
				loading={status === 'pending' || !postageOptions || props.loading}
				valueType='number'
				pagination={{
					itemCounts: postageItemCounts,
					fetchNextPage,
					isFetchingNextPage,
					hasNextPage,
					isStale,
					isRefetching,
				}}
				displayType={ComboBoxTypeEnum.Single}
				displayItemLabelType={ComboBoxItemDisplayEnum.Label}
				forwardItemCallback={(e) => HandleItemsChanged(e.value)}
				forwardValueCallback={(e) => HandleValueChanged(e)}
				forwardValuesCallback={(e) => HandleValueChanged(e)}
				disabled={props.disabled || (!props.activeAddress && !props.alwaysEnabled)}
				formReset={props.reset}
			/>
			{postageError && <AlertBanner title={postageError} colour={Colour.Alert} />}
		</>
	);
}

function PostageControl(props: PostageControlType) {
	const [currentCountryCode, setCurrentCountryCode] = useState<string>(
		props.activeAddress?.object.countryCode ?? 'GB',
	);

	useEffect(() => {
		if (props.activeAddress && props.activeAddress.object.countryCode !== currentCountryCode) {
			setCurrentCountryCode(props.activeAddress.object.countryCode);
		}
	}, [props.activeAddress]);

	return <PostageControlInner {...props} currentCountryCode={currentCountryCode} />;
}

export { PostageControl };
